import React, { useState, useEffect, useRef } from "react";
import { Form, Grid, Message } from 'semantic-ui-react';
import { Captcha } from "reactjs-captcha";
import { captchaSettings } from "reactjs-captcha";
import translate from 'react-i18next/dist/commonjs/translate';
import { connect } from 'react-redux';
import { resetPasswordSendEmail, resetPassword } from "./ResetPasswordActions";

captchaSettings.set({ 
  captchaEndpoint: 
    '/ndapmoci/simple-captcha-endpoint' 
});

const resetPasswordFormStates = {
  REQUEST_CODE: "REQUEST_CODE",
  CHANGE_PASSWORD: "CHANGE_PASSWORD",
  CHANGE_PASSWORD_DONE: "CHANGE_PASSWORD_DONE"
};


function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const resetPasswordValidationErrorsCodes = {
  USERNAME_IS_REQUIRED: "resetUsernameIsRequired",
  CAPTCHA_IS_REQUIRED: "resetCaptchaIsRequired",
  RESET_CODE_IS_REQUIRED: "resetResetCodeIsRequired",
  NEW_PASSWORD_IS_REQUIRED: "resetNewPasswordIsRequired",
  CONFIRM_PASSWORD_IS_REQUIRED: "resetConfirmPasswordIsRequired",
  NEW_CONFIRM_PASSWORD_NOT_MATCH: "resetNewConfirmPasswordNotMatch",
  PASSWORD_NOT_VALID: "passwordNotValid"
};

const ResetPasswordFormComponent = (props) => {

    const [username, setUsername] = useState("");
    const [formState, setFormState] = useState(resetPasswordFormStates.REQUEST_CODE);
    const [resetCode, setResetCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [formValidationErrorCode, setFormValidationErrorCode] = useState("");


    const { t } = props;

    const requestCodeDonePrevious = usePrevious(props.requestCodeDone);

    useEffect(() => { 
      if(requestCodeDonePrevious !== undefined && requestCodeDonePrevious !== props.requestCodeDone && props.requestCodeDone) {
        setFormState(resetPasswordFormStates.CHANGE_PASSWORD);
      }
      return () => {
        setFormState(resetPasswordFormStates.REQUEST_CODE);
      };
    }, [props.requestCodeDone]);

    const requestResetPasswordDonePrevious = usePrevious(props.requestResetPasswordDone);

    useEffect(() => { 
      if(requestResetPasswordDonePrevious !== undefined && requestResetPasswordDonePrevious !== props.requestResetPasswordDone && props.requestResetPasswordDone) {
        setFormState(resetPasswordFormStates.CHANGE_PASSWORD_DONE);
      }
      return () => {
        setFormState(resetPasswordFormStates.REQUEST_CODE);
      };
    }, [props.requestResetPasswordDone]);

    const requestResetCodeStart = () => {
      
      const validationResult = validateForm();
      if(!validationResult.valid) {
        setFormValidationErrorCode(validationResult.errorCode);
        return;
      }

      const { dispatch } = props;
      let userEnteredCaptchaCode = this.captcha.getUserEnteredCaptchaCode(); 
      let captchaId = this.captcha.getCaptchaId();
      dispatch(resetPasswordSendEmail(username, userEnteredCaptchaCode, captchaId));

    };

    const onUsernameChanged = (event, data) => {
      setFormValidationErrorCode("");
      setUsername(data.value.trim());
    };

    const onResetCodeChanged = (event, data) => {
      setFormValidationErrorCode("");
      setResetCode(data.value.trim());
    };

    const onNewPasswordChanged = (event, data) => {
      setFormValidationErrorCode("");
      setNewPassword(data.value.trim());
    }

    const onConfirmNewPasswordChanged = (event, data) => {
      setFormValidationErrorCode("");
      setConfirmNewPassword(data.value.trim());
    }

    const onInputChangeCaptcha = (event, data) => {
      setFormValidationErrorCode("");
    };

    const resetPasswordStart = () => {
      
      const validationResult = validateForm();
      if(!validationResult.valid) {
        setFormValidationErrorCode(validationResult.errorCode);
        return;
      }

      const { dispatch } = props;
      dispatch(resetPassword(username, newPassword, resetCode));

    };
    
    const passwordRegEx = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])\w{6,}$/;; //at least six characters that are letters, numbers or the underscore

    const validateForm = () => {

      if(formState === resetPasswordFormStates.REQUEST_CODE) {
        if(!username) {
          return { valid: false, errorCode: resetPasswordValidationErrorsCodes.USERNAME_IS_REQUIRED };
        }
        if(!this.captcha.getUserEnteredCaptchaCode()) {
          return  { valid: false, errorCode: resetPasswordValidationErrorsCodes.CAPTCHA_IS_REQUIRED };
        }
      }

      if(formState === resetPasswordFormStates.CHANGE_PASSWORD) {
        if(!resetCode) {
          return  { valid: false, errorCode: resetPasswordValidationErrorsCodes.RESET_CODE_IS_REQUIRED };
        }
        if(!newPassword) {
          return  { valid: false, errorCode: resetPasswordValidationErrorsCodes.NEW_PASSWORD_IS_REQUIRED };
        }
        if(!confirmNewPassword) {
          return  { valid: false, errorCode: resetPasswordValidationErrorsCodes.CONFIRM_PASSWORD_IS_REQUIRED };
        }
        if(newPassword !== confirmNewPassword) {
          return  { valid: false, errorCode: resetPasswordValidationErrorsCodes.NEW_CONFIRM_PASSWORD_NOT_MATCH };
        }
        if (!passwordRegEx.test(newPassword)) {
          return  { valid: false, errorCode: resetPasswordValidationErrorsCodes.PASSWORD_NOT_VALID };
        }
      }

      return {valid: true};
    };

    return (
      <div className='login-form'>
        <Grid textAlign='center' style={{ height: '100%' }} verticalAlign='middle'>
          <Grid.Column style={{ maxWidth: 450 }}>
            {formState === resetPasswordFormStates.REQUEST_CODE &&
              <Form size='large' onSubmit={requestResetCodeStart}>
                <Form.Input
                  name="username"
                  fluid
                  icon='user'
                  iconPosition='left'
                  placeholder={t('username')}
                  label={t('resetPasswordDescription')}
                  className="text-align-left"
                  onChange={onUsernameChanged}
                  autoComplete="new-password"
                />
                <Form.Input
                  name="code"
                  id="yourFirstCaptchaUserInput"
                  fluid
                  icon='qrcode'
                  iconPosition='left'
                  placeholder={t('captchaCode')}
                  onChange={onInputChangeCaptcha}
                  autoComplete="new-password"
                />
                <Captcha captchaStyleName="yourFirstCaptchaStyle" 
                      ref={(captcha) => {this.captcha = captcha}}  />
                <Form.Button compact fluid size='large'>
                  {t('proceed')}
                </Form.Button>
                <div className="back-to-login-link">
                  <span className="label-as-link" onClick={props.onCancelPasswordResetClick}>{t('backToLogin')}</span>
                </div>            
              </Form>
            }
            {formState === resetPasswordFormStates.CHANGE_PASSWORD &&
              <Form size='large' onSubmit={resetPasswordStart}>
                <Form.Input
                  name="code"
                  fluid
                  iconPosition='left'
                  placeholder={t('code')}
                  className="text-align-left"
                  onChange={onResetCodeChanged}
                  autoComplete="new-password"
                />
                <Form.Input
                  name="password"
                  fluid
                  icon="lock"
                  type="password"
                  iconPosition='left'
                  placeholder={t('newPassword')}
                  onChange={onNewPasswordChanged}
                  autoComplete="new-password"
                />
                <Form.Input
                  name="confirmPassword"
                  fluid
                  icon="lock"
                  type="password"
                  iconPosition='left'
                  placeholder={t('confirmPassword')}
                  onChange={onConfirmNewPasswordChanged}
                  autoComplete="new-password"
                />
                <Form.Button compact fluid size='large'>
                  {t('changePassword')}
                </Form.Button>
                <div className="back-to-login-link">
                  <span className="label-as-link" onClick={props.onCancelPasswordResetClick}>{t('backToLogin')}</span>
                </div>
              </Form>
            }
            {formValidationErrorCode &&
              <Message error>
                {t(formValidationErrorCode)}
              </Message>
            }
            {formState === resetPasswordFormStates.CHANGE_PASSWORD_DONE &&
              <React.Fragment>
                <Message positive>
                  {t("resetPasswordSuccess")}
                </Message>
                <div className="back-to-login-link">
                  <span className="label-as-link" onClick={props.onCancelPasswordResetClick}>{t('doLoginAfterReset')}</span>
                </div>
              </React.Fragment>
            }
          </Grid.Column>
        </Grid>
      </div>
    );
};

const mapStateToProps = state => {
  return {
    requestCodeDone: state.resetPasswordReducer.requestCodeDone,
    requestResetPasswordDone: state.resetPasswordReducer.requestResetPasswordDone
  };
};

const ResetPasswordFormContainer = connect(mapStateToProps)(ResetPasswordFormComponent)

export default translate()(ResetPasswordFormContainer);


// WEBPACK FOOTER //
// ./src/features/common/login/reset-password-form/ResetPasswordFormContainer.jsx